import { debounce } from 'lodash-es';
import callClientPortal from './callClientPortal';
import getJWT from './getJWT';
import { getCourseId, get, set, getFirm, getLocation } from './SCORM';
import useUcEvent from './useUcEvent';
import extractTitle, { extractClTitle } from './extractTitle';
import extractBaseCourse, { extractBaseClCourse } from './extractBaseCourse';
import processYaml from './processYaml';
import jsYaml from 'js-yaml';
import useTitle from '../stores/useTitle';
import fetchContent from './fetchContent';
import getContentBase from './getContentBase';

const LATEST = encodeURIComponent(`'metadata'->'createdDate':desc`);
const isClientEditMode = Boolean(getFirm() && get('clientEditMode'));
const portalApiBase =
  (window.VITE_API_BASE ||
    import.meta.env.VITE_API_BASE ||
    window.location.origin.replace(
      /^(https?:\/\/)(\w+)(\..*vinciworks.*\.com)$/,
      '$1portal$3'
    )) + '/api';
// const EARLIEST = encodeURIComponent(`'metadata'->'createdDate':asc`);

/**
 * Computes a Client Portal query param to match a given course
 */
const getCourseQueryParam = ({ courseId, firmCode }: CourseAndFirm) =>
  `[[{"field":"courseId","op":"=","val":${courseId}},${
    firmCode
      ? `{"field":"firmCode","op":"=","val":"${firmCode}"}`
      : '{"field":"type","op":"=","val":"course"}'
  }]]`;

// Query parameter builder for resources (uses vinciResourceId instead of courseId)
const getResourceQueryParam = ({
  vinciResourceId
}: {
  vinciResourceId: number;
}) =>
  `[[{"field":"vinciResourceId","op":"=","val":${vinciResourceId}},{"field":"type","op":"=","val":"resource"}]]`;

export const createNewDraft = async (
  draft: CourseDraft,
  { overwrite = false } = {}
) => {
  draft.author = getJWT().userProfile.email;
  draft.type = draft.type || 'course';
  draft.ubercourseVersion = draft.ubercourseVersion || 'latest';

  // For resources, don't set firmCode, yaml, or courseId at all
  if (draft.type !== 'resource') {
    draft.firmCode = draft.firmCode || '';
    // Normalize to the project-standard YAML format (see updateDraft).
    if (draft.yaml && draft.yaml.trim()) {
      try {
        draft.yaml = processYaml(draft.yaml).yaml;
      } catch (error) {
        // keep the raw yaml
      }
    }
  } else {
    // Ensure yaml, firmCode, and courseId are not included for resources
    delete (draft as any).yaml;
    delete (draft as any).firmCode;
    delete (draft as any).courseId;
  }

  /**
   * if we haven't provided a draft title then extract it from the draft and base course yaml
   * Skip YAML title extraction for resources (they have title from form)
   */
  if (draft.type !== 'resource' && !draft.title) {
    try {
      const baseCourseId =
        draft?.activityName === 'Conversational Learning'
          ? extractBaseClCourse(draft.yaml)
          : extractBaseCourse(draft.yaml);
      const baseCourse = await getLatestSaved({
        courseId: baseCourseId ?? draft.courseId
      });
      const title =
        draft?.activityName === 'Conversational Learning'
          ? extractClTitle(draft.yaml, baseCourse?.yaml)
          : extractTitle(draft.yaml, baseCourse?.yaml);
      if (title) draft.title = title;
    } catch (error) {
      // do nothing
    }
  }

  let jsonString = JSON.stringify(draft);
  const body = new FormData();
  body.append('jsonContent', jsonString);

  try {
    const r = await callClientPortal<CourseDraft>('/bigdrafts', {
      method: 'POST',
      body
    });

    if (r.response.status === 409 && overwrite) {
      return callClientPortal<CourseDraft>('/bigdrafts', {
        method: 'PUT',
        body
      });
    }

    return r;
  } catch (e) {
    if (e.message.includes('409') && overwrite) {
      return callClientPortal<CourseDraft>('/bigdrafts', {
        method: 'PUT',
        body
      });
    } else {
      throw e;
    }
  }
};

export const deleteDraft = (uuid: string) => {
  return callClientPortal(`/drafts/${uuid}`, { method: 'DELETE' });
};

// @ts-ignore
window.deleteDraft = deleteDraft;

export const getLatestDraft = async ({ courseId, firmCode }: CourseAndFirm) => {
  const query = getCourseQueryParam({ courseId, firmCode });
  const endpoint = `/drafts?limit=1&query=${query}`;
  const response = await callClientPortal<CourseResponse>(endpoint);

  return response.data.data.results?.pop();
};

export const getCurrentTenantAddPageEnabled = async () => {
  const response = await callClientPortal<any>('/cp/v1/company', undefined, {
    base: portalApiBase
  });
  const tenantInfo = response.data.data.results;

  const editingFeatures =
    tenantInfo?.customerInfo?.editingFeatures ?? tenantInfo?.editingFeatures;

  // Legacy tenants without editingFeatures default to enabled
  if (!editingFeatures) return true;

  return Boolean(editingFeatures.addPage);
};

export const getLatestSaved = async (
  { courseId, firmCode }: CourseAndFirm,
  { compact = false } = {}
) => {
  const query = getCourseQueryParam({ courseId, firmCode });
  let endpoint = `/saved?limit=5&distinct=true&sort=${LATEST}&query=${query}`;
  if (compact) endpoint += '&compact=true';
  const response = await callClientPortal<CourseResponse>(endpoint);
  const results = response.data.data.results;

  if (!results) return;

  // results are sorted newest-first, so the first valid match is the latest
  return results.find(
    // ensure we dont use corrupted versions, e.g. type course with a firmCode
    r => (r.firmCode || '') === (firmCode || '')
  );
};
export const getLatestPublished = async ({
  courseId,
  firmCode
}: CourseAndFirm) => {
  const base = getContentBase();
  const customFolder = `${base}/custom/${firmCode?.toLowerCase()}`;
  const baseFolder = `${base}`;

  const urls = firmCode
    ? [
        `${customFolder}/${firmCode}_${courseId}.json`,
        `${customFolder}/${firmCode.toLowerCase()}_${courseId}.json`,
        `${customFolder}/${firmCode.toUpperCase()}_${courseId}.json`
      ]
    : [`${baseFolder}/${courseId}.json`];

  for (const url of urls) {
    const raw = await fetchContent(url).catch(() => '');
    if (!raw) continue;

    try {
      let json = JSON.parse(raw);
      // The published payload is occasionally a stringified-JSON string (double
      // encoded). Parse once more so callers always get the Content object.
      if (typeof json === 'string') json = JSON.parse(json);
      return json;
    } catch (error) {
      console.error(`Failed to parse published course JSON from ${url}`, error);
    }
  }

  return null;
};

window.getLatestSaved = getLatestSaved;

let originalDraft: CourseDraft | null = null;

export const getLatestDraftOrSaved = async ({
  courseId,
  firmCode
}: CourseAndFirm) => {
  const [latestDraft, latestSaved] = await Promise.all([
    getLatestDraft({ courseId, firmCode }),
    getLatestSaved({ courseId, firmCode })
  ]);

  // TODO lock UI if latest draft is someone else's
  const latest =
    latestDraft && latestSaved
      ? // if both exist, use newer; tie goes to the draft (uncommitted work)
        latestDraft?.metadata.updatedDate >= latestSaved?.metadata.createdDate
        ? latestDraft
        : latestSaved
      : latestDraft || latestSaved;

  if (latest) originalDraft = latest;

  return latest;
};

// Get latest resource draft or saved (queries by vinciResourceId)
export const getLatestResourceDraftOrSaved = async ({
  vinciResourceId
}: {
  vinciResourceId: number;
}) => {
  const query = getResourceQueryParam({ vinciResourceId });

  // Query drafts
  const draftEndpoint = `/drafts?limit=1&query=${query}`;
  const draftResponse = await callClientPortal<CourseResponse>(draftEndpoint);
  const latestDraft = draftResponse.data.data.results?.pop();

  // Query saved
  const savedEndpoint = `/saved?limit=5&distinct=true&sort=${LATEST}&query=${query}&compact=true`;
  const savedResponse = await callClientPortal<CourseResponse>(savedEndpoint);
  const results = savedResponse.data.data.results;
  // results are sorted newest-first, so the first valid match is the latest
  const latestSaved = results?.find(r => r.type === 'resource');

  // Return the newer of the two
  const latest =
    latestDraft && latestSaved
      ? latestDraft?.metadata.updatedDate >= latestSaved?.metadata.createdDate
        ? latestDraft
        : latestSaved
      : latestDraft || latestSaved;

  if (latest) originalDraft = latest;

  return latest;
};

export const getAllVersions = async (
  { courseId, firmCode }: CourseAndFirm,
  compact: boolean = false,
  limit: number = 9999
) => {
  const query = getCourseQueryParam({ courseId, firmCode });
  let endpoint = `/saved?query=${query}&sort=${LATEST}`;
  if (compact) endpoint += '&compact=true';
  if (limit) endpoint += `&limit=${limit}`;
  const response = await callClientPortal<CourseResponse>(endpoint);
  const results = response.data.data.results;
  if (!results) return [];
  results.sort((a, b) => b.metadata.createdDate - a.metadata.createdDate);

  return results;
};

/**
 * Fetch a single saved version's full body (yaml/css) by its unique id.
 *
 * Every saved row carries a stable UUID at jsonb.id (the row PK, mirrored into
 * the jsonb by the set_id_injson_saved_items_table trigger), which is present
 * even in compact list responses. `{field:"id"}` normalises to `jsonb->>'id'`
 * for the /saved route, so this is an exact-match, non-compact lookup — the way
 * the History page pulls a version's yaml/css on demand instead of pre-fetching
 * every body. Scoped by courseId + firmCode/type (mirroring getCourseQueryParam)
 * to keep the lookup selective.
 */
export const getVersionById = async ({
  courseId,
  firmCode,
  id
}: CourseAndFirm & { id: string }) => {
  const clauses = [
    { field: 'courseId', op: '=', val: courseId },
    firmCode
      ? { field: 'firmCode', op: '=', val: firmCode }
      : { field: 'type', op: '=', val: 'course' },
    { field: 'id', op: '=', val: id }
  ];
  const query = encodeURIComponent(JSON.stringify([clauses]));
  const response = await callClientPortal<CourseResponse>(
    `/saved?query=${query}&limit=1`
  );
  return response.data.data.results?.[0];
};

export const countCustomisations = async (courseId: number) => {
  const query1 = encodeURIComponent(
    JSON.stringify([[{ field: 'baseCourseId', op: '=', val: courseId }]])
  );

  const query2 = encodeURIComponent(
    JSON.stringify([
      [
        { field: 'courseId', op: '=', val: courseId },
        { field: 'type', op: '=', val: 'custom' }
      ]
    ])
  );

  const endpoint1 = `/saved?limit=200&distinct=true&sort=%27metadata%27-%3E%27createdDate%27%3Adesc&query=${query1}`;
  const endpoint2 = `/saved?limit=200&distinct=true&sort=%27metadata%27-%3E%27createdDate%27%3Adesc&query=${query2}`;

  const response1 = await callClientPortal<CourseResponse>(endpoint1);
  const response2 = await callClientPortal<CourseResponse>(endpoint2);
  const results1 = response1.data.data.results || [];
  const results2 = response2.data.data.results || [];
  const results = [...results1, ...results2];
  // const results = results2;

  const report: CustomReport = {
    courseId,
    total: 0,
    translations: 0,
    patches: 0,
    audio: 0,
    patchedAudio: 0,
    patchedTranslations: 0,
    list: []
  };

  if (!results) return report;

  for (const result of results) {
    const hasTranslations = result.yaml.includes('languages:');
    const hasPatches = result.yaml.includes('patches:');
    const hasAudio =
      result.yaml.includes('audio:') || result.yaml.includes('$course.audio');
    const hasPatchedAudio = hasPatches && hasAudio;
    const hasPatchedTranslations = hasPatches && hasTranslations;

    let languages: string[] = [];

    if (hasTranslations) {
      const yaml = await import('js-yaml');
      const parsed = yaml.load(result.yaml) as Partial<Content>;
      languages = parsed.languages || [];
    }

    if (hasTranslations) report.translations++;
    if (hasPatches) report.patches++;
    if (hasAudio) report.audio++;
    if (hasPatchedAudio) report.patchedAudio++;
    if (hasPatchedTranslations) report.patchedTranslations++;

    report.list.push({
      firmCode: result.firmCode as string,
      courseId: result.courseId,
      createdDate: result.metadata.createdDate,
      updatedDate: result.metadata.updatedDate,
      yaml: result.yaml || '',
      css: result.compiledCss || '',
      type: result.type || 'custom',
      title: extractTitle(result.yaml),
      hasTranslations,
      languages,
      hasPatches,
      hasAudio,
      hasPatchedAudio,
      hasPatchedTranslations
    });
    // report.list.push({
    //   firmCode: result.firmCode as string,
    //   courseId: result.courseId,
    //   createdDate: result.metadata.createdDate,
    //   title: extractTitle(result.yaml),
    //   hasTranslations,
    //   languages,
    //   hasPatches,
    //   hasAudio,
    //   hasPatchedAudio,
    //   hasPatchedTranslations
    // });
    // report.list.push({
    //   firmCode: result.firmCode as string,
    //   courseId: result.courseId,
    //   createdDate: result.metadata.createdDate,
    //   title: extractTitle(result.yaml),
    //   hasTranslations,
    //   languages,
    //   hasPatches,
    //   hasAudio,
    //   hasPatchedAudio,
    //   hasPatchedTranslations
    // });
    report.total++;
  }

  return report;
};

window.countCustomisations = countCustomisations;

export const updateDraft = async (
  draft: CourseDraft,
  baseDraft?: CourseDraft
) => {
  draft.author = getJWT().userProfile.email;
  draft.type = draft.type || 'course';
  draft.ubercourseVersion = draft.ubercourseVersion || 'latest';

  // Normalize YAML into the project-standard format before it ever leaves the
  // client. Every save path funnels through updateDraft (commitDraft calls it
  // too), so this is the single chokepoint that keeps the full AT, AT Lite,
  // edit-agent and auto-commit outputs identically formatted - matching the
  // backend canonical serializer and avoiding redundant reformatting commits.
  // Never block a save: on a parse error, keep the raw YAML.
  if (draft.type !== 'resource' && draft.yaml && draft.yaml.trim()) {
    try {
      draft.yaml = processYaml(draft.yaml).yaml;
    } catch (error) {
      // keep the raw yaml
    }
  }

  /**
   * Try to extract the course title from either:
   * - the course title store (edit mode)
   * - the yaml + base yaml (Author: Editing)
   *
   * For resources, skip title extraction if title is already set (comes from form)
   */

  let gotTitle: boolean = false;

  // For resources, preserve existing title if it's already set
  if (draft.type === 'resource' && draft.title && draft.title.trim()) {
    gotTitle = true;
  }

  if (
    !gotTitle &&
    isClientEditMode &&
    draft?.activityName !== 'Conversational Learning'
  ) {
    try {
      const titleStore = useTitle();
      const storeTitle = titleStore.getFullTitle();
      draft.title = storeTitle;
      gotTitle = true;
    } catch (error) {
      // do nothing
    }
  }

  // Skip YAML title extraction for resources (they don't have YAML)
  if (!gotTitle && draft.type !== 'resource') {
    try {
      const baseCourseId =
        draft?.activityName === 'Conversational Learning'
          ? extractBaseClCourse(draft.yaml)
          : extractBaseCourse(draft.yaml);

      // use the provided base draft, otherwise get the latest saved base course
      let baseCourse =
        baseDraft ??
        (await getLatestSaved({
          courseId: baseCourseId ?? draft.courseId
        }));

      // In AT Lite the client does not have permission to get the saved base course from the DB, so we fallback to the latest published version of the base JSON
      if (!baseCourse) {
        // If we couldn't find the base course, try to fetch it from the latest published content
        const basePublishedJson = await getLatestPublished({
          courseId: baseCourseId ?? draft.courseId
        });

        // this is a temp ref used to get the title from the base course yaml
        baseCourse = {
          courseId: baseCourseId ?? draft.courseId,
          // convert the published JSON to yaml
          yaml: jsYaml.dump(basePublishedJson, { lineWidth: -1 })
        };
      }

      // we need BOTH the draft and base yaml to make sure we include any subtitle text
      if (baseCourse) {
        const title =
          draft?.activityName === 'Conversational Learning'
            ? extractClTitle(draft?.yaml, baseCourse?.yaml)
            : extractTitle(draft?.yaml, baseCourse?.yaml);

        if (title) {
          draft.title = title;
          gotTitle = true;
        }

        // update the base course id if it is different from the draft's version
        if (baseCourseId && baseCourseId !== draft.courseId)
          draft.baseCourseId = baseCourseId;
      }
    } catch (error) {
      // do nothing
    }

    // if we still don't have a title, just use the draft yaml and any base draft yaml if provided
    if (!gotTitle) {
      draft.title =
        draft?.activityName === 'Conversational Learning'
          ? extractClTitle(draft?.yaml, baseDraft?.yaml)
          : extractTitle(draft?.yaml, baseDraft?.yaml);
    }
  }

  // For resources, ensure title is preserved if it was set
  if (draft.type === 'resource' && !draft.title) {
    console.warn(
      'Resource draft has no title - this should not happen for new resources'
    );
  }

  const body = new FormData();
  body.append('jsonContent', JSON.stringify(draft));

  return callClientPortal(`/bigdrafts/${draft.courseId}`, {
    method: 'PUT',
    body
  });
};

type CommitParam = {
  draft: CourseDraft;
  commitMessage: string;
  baseCourse?: CourseDraft;
};

export const commitDraft = async (
  { draft, commitMessage }: CommitParam,
  { skipUpdateDraft = false } = {}
) => {
  const { courseId, firmCode } = draft;
  if (!skipUpdateDraft) await updateDraft(draft);

  // For resources, use vinciResourceId as the identifier; for courses, use courseId
  const identifier = draft.vinciResourceId || draft.courseId;
  const isResource = draft.type === 'resource';

  return callClientPortal(`/save/${identifier}`, {
    method: 'POST',
    body: JSON.stringify({
      commitMessage,
      courseId: identifier, // Backend will determine which field to use based on isResource
      firmCode: firmCode || '',
      isResource // Flag to help backend determine query type
    })
  });
};

type PublishParam = {
  draft: CourseDraft;
  publishTo: 'production' | 'test';
  publishData?: Partial<PublishBody>;
};

export const publishCourse = async ({
  draft,
  publishTo = 'production',
  publishData = {}
}: PublishParam) => {
  const { courseId, type } = draft;

  const body: PublishBody = {
    courseId,
    publishTo,
    level: 'minor',
    message: '',
    internal: '[published in author ui]',
    ...publishData
  };

  if (type !== 'course') body.firmCode = draft.firmCode;

  const response = await callClientPortal(`/publish/${type}`, {
    method: 'POST',
    body: JSON.stringify(body)
  });

  return response;
};

const encodePatches = (patches: Content['patches']): string =>
  encodeURIComponent(JSON.stringify(patches));

// const decodePatches = (
//   encoded: string | Content['patches']
// ): Content['patches'] =>
//   typeof encoded === 'string'
//     ? JSON.parse(decodeURIComponent(encoded))
//     : encoded;

export function getSuspendEdits(): Partial<Content> {
  return {
    // patches: decodePatches((get('patches') as string) || '{}'),
    // hidden: JSON.parse((get('hidden') as string) || '{}')
  };
}

let latestSavedDraft: CourseDraft | null = null;

export const commitClientEdits = async ({
  pageId = getLocation() || '',
  skipUpdateDraft = false
} = {}) => {
  if (!latestSavedDraft) return;
  const commitMessage = `Self-service edits on page #${pageId}`;

  await commitDraft(
    { draft: latestSavedDraft, commitMessage },
    { skipUpdateDraft }
  );

  latestSavedDraft = null;
};

export const searchCourses = async (string: string, field = 'yaml') => {
  const query = encodeURIComponent(
    JSON.stringify([
      [{ field, op: 'LIKE', val: encodeURIComponent(`%${string}%`) }]
    ])
  );

  const response = await callClientPortal(
    `/saved?limit=99999&query=${query}&distinct=true&compact=true&sort='metadata'->'updatedDate':desc`
  );

  return response.data.data.results;
};

window.addEventListener('beforeunload', () =>
  commitClientEdits({ skipUpdateDraft: true })
);

const commitDraftDebounced = debounce(commitClientEdits, 30 * 1000, {
  leading: false,
  trailing: true
});

export async function updateCustomClientAPI(custom: Custom) {
  if (isClientEditMode) {
    const jsYaml = await import('js-yaml');
    // updateDraft normalizes the YAML into the canonical format, so we only
    // need a raw dump here.
    const yamlString = jsYaml.dump(custom, { lineWidth: -1 });

    const type = originalDraft?.type === 'bespoke' ? 'bespoke' : 'custom';

    const body: CourseDraft = {
      ...originalDraft,
      type,
      courseId: getCourseId(),
      firmCode: getFirm(),
      yaml: yamlString
    };

    await updateDraft(body as CourseDraft);
    latestSavedDraft = body;
    commitDraftDebounced();
  } else {
    set('patches', encodePatches(custom.patches));
    set('hidden', JSON.stringify(custom.hidden));
  }
}

if (isClientEditMode) {
  // commit when changing pages
  useUcEvent('uc:pageleave', async e => {
    commitClientEdits({ pageId: e.detail.pageId });
  });

  // commit when leaving edit mode
  useUcEvent('uc:editmodechange', async e => {
    if (e.detail.mode !== 'edit') commitClientEdits();
  });
}

export const getTranslations = async ({
  courseId = getCourseId(),
  firmCode = getFirm(),
  to
}: {
  courseId?: number;
  firmCode?: string;
  to: string;
}) => {
  const response = await callClientPortal<PhraseRow>(
    `/translations?limit=9999&courseId=${courseId}&firmCode=${firmCode}&to=${to}`
  );

  return response.data.data.results;
};

export const uploadTsv = async ({
  courseId,
  firmCode = '',
  to,
  type,
  provider = '',
  file
}: {
  courseId: number | string;
  firmCode?: string;
  to: string;
  type: string;
  provider?: string;
  file: File;
}) => {
  const body = new FormData();
  body.append('tsv', file);
  body.append('courseId', `${courseId}`);
  body.append('firmCode', firmCode);
  body.append('to', to);
  body.append('type', type);
  body.append('provider', provider);
  const response = await callClientPortal(`/translations/tsv`, {
    method: 'POST',
    body
  });

  return response;
};

export const updateCustomPhrase = async ({
  courseId = getCourseId(),
  firmCode = getFirm(),
  to,
  phrase
}: {
  courseId?: number | string;
  firmCode?: string;
  to: string;
  phrase: Phrase;
}) => {
  const { key, english, translated } = phrase;
  const tsv = `Key\tEnglish\t${to}\n${key}\t${english}\t${translated}\n`;
  const type = 'text/tab-separated-values';
  const file = new File([tsv], `${key}.tsv`, { type });
  const user = getJWT().userProfile.email;
  const provider = user ? `edited:${user}` : 'edited';

  return uploadTsv({
    courseId,
    firmCode,
    to,
    type: 'untrusted',
    provider,
    file
  });
};

export const createAiTranslations = async ({
  courseId,
  firmCode,
  to,
  dryRun = false,
  refreshAi = false,
  skipAi = false,
  keys = '',
  provider = 'azure'
}: TranslationParams) => {
  let route = firmCode
    ? `/translations/ai?courseId=${courseId}&to=${to}&firmCode=${firmCode}`
    : `/translations/ai?courseId=${courseId}&to=${to}`;

  if (dryRun) route += '&dryRun=true';
  if (skipAi) route += '&skipAi=true';
  if (refreshAi) route += '&refreshAi=true';
  if (keys) route += `&keys=${encodeURIComponent(keys)}`;
  if (provider) route += `&provider=${encodeURIComponent(provider)}`;

  return callClientPortal(route, { method: 'POST' });
};

if (import.meta.env.DEV)
  window.resetCustomization = async () => {
    const courseId = getCourseId();
    await updateCustomClientAPI({ patches: {}, courseId });
  };

// @ts-ignore
window.getLatestDraft = getLatestDraft;
// @ts-ignore
window.updateDraft = updateDraft;
// @ts-ignore
window.createNewDraft = createNewDraft;
// @ts-ignore
window.commitDraft = commitDraft;
// @ts-ignore
window.callClientPortal = callClientPortal;
// @ts-ignore
window.searchCourses = searchCourses;

export const getNextBaseCourseId = async (
  forResource = false
): Promise<number> => {
  try {
    const queryParam = forResource ? '?forResource=true' : '';
    const response = await callClientPortal<{ nextCourseId: number }>(
      `/saved/next-base-course-id${queryParam}`
    );
    return response.data.data.nextCourseId;
  } catch (error) {
    console.error('Error getting next base course ID:', error);
    // Fallback to 5000 if API fails
    return 5000;
  }
};

export const getNextBespokeCourseId = async (
  firmCode: string
): Promise<number> => {
  try {
    const response = await callClientPortal<{ nextCourseId: number }>(
      `/saved/next-bespoke-course-id?firmCode=${firmCode}`
    );
    return response.data.data.nextCourseId;
  } catch (error) {
    console.error('Error getting next bespoke course ID:', error);
    // Fallback to 1001000 if API fails
    return 1001000;
  }
};

// @ts-ignore
window.getNextBaseCourseId = getNextBaseCourseId;
// @ts-ignore
window.getNextBespokeCourseId = getNextBespokeCourseId;
